import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { userService, TermsDocument } from '../../services/userService';

interface Props {
  open: boolean;
  onAccepted: () => void;
}

const TermsAcceptanceModal: React.FC<Props> = ({ open, onAccepted }) => {
  const [terms, setTerms] = useState<TermsDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setChecked(false);
      userService.getActiveTerms().then((t) => {
        setTerms(t);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [open]);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await userService.acceptTerms();
      onAccepted();
    } catch {
      // stay open
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} maxWidth="md" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {terms?.title || 'Terms & Conditions'}
        {terms?.version && (
          <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
            v{terms.version}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : terms ? (
          <Box
            sx={{
              maxHeight: 400,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              fontSize: '0.875rem',
              lineHeight: 1.7,
            }}
          >
            {terms.body}
          </Box>
        ) : (
          <Typography color="text.secondary">No terms document found.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              disabled={!terms || submitting}
            />
          }
          label="I have read and agree to the Terms & Conditions"
        />
        <Button
          variant="contained"
          fullWidth
          disabled={!checked || submitting || !terms}
          onClick={handleAccept}
        >
          {submitting ? <CircularProgress size={20} /> : 'Accept & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAcceptanceModal;
